import { useEffect, useState } from "react";
import { withNamespaces } from "react-i18next";
import {
    Button,
    Input,
    Label,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
} from "reactstrap";

import { isRejectionReasonValid } from "../../helpers";

const ApprovalConfirmationModal = ({
    t,
    isOpen,
    toggle,
    mode = "approve",
    roleTitle,
    isChangeDecision = false,
    loading = false,
    onConfirm,
}) => {
    const [reason, setReason] = useState("");
    const [showReasonError, setShowReasonError] = useState(false);
    const isReject = mode === "reject";
    const reasonIsValid = isRejectionReasonValid(reason);

    useEffect(() => {
        if (!isOpen) {
            setReason("");
            setShowReasonError(false);
        }
    }, [isOpen]);

    const handleToggle = () => {
        setReason("");
        setShowReasonError(false);
        toggle();
    };

    const handleConfirm = () => {
        if (isReject && !reasonIsValid) {
            setShowReasonError(true);
            return;
        }

        onConfirm({ reason: String(reason).trim() });
    };

    return (
        <Modal isOpen={isOpen} toggle={handleToggle} centered>
            <ModalHeader toggle={handleToggle}>
                {isChangeDecision
                    ? t("Change decision")
                    : isReject
                    ? t("Reject")
                    : t("Confirm Compliance Approval")}
            </ModalHeader>
            <ModalBody>
                {isReject ? (
                    <>
                        <Label className="form-label" for="required-approval-reject-reason">
                            {t("Reason")}
                        </Label>
                        <Input
                            id="required-approval-reject-reason"
                            type="textarea"
                            rows="3"
                            value={reason}
                            invalid={showReasonError && !reasonIsValid}
                            onChange={(event) => {
                                setReason(event.target.value);
                                if (showReasonError) {
                                    setShowReasonError(false);
                                }
                            }}
                        />
                        {showReasonError && !reasonIsValid ? (
                            <div className="text-danger font-size-13 mt-1">
                                {t("Please provide a reason for rejection.")}
                            </div>
                        ) : null}
                    </>
                ) : (
                    <>
                        <p>
                            {t(
                                "You are about to approve this compliance review as {{role}}",
                                {
                                    role: roleTitle,
                                }
                            )}
                        </p>
                        <p>
                            {t(
                                "Your approval will be recorded in the audit trail and considered during the final compliance decision process."
                            )}
                        </p>
                        <p className="mb-0">
                            {t(
                                "Please confirm that you have reviewed the associated risks, remediation tasks, and compliance recommendations before proceeding."
                            )}
                        </p>
                    </>
                )}
            </ModalBody>
            <ModalFooter>
                <Button
                    color="secondary"
                    type="button"
                    onClick={handleToggle}
                    disabled={loading}
                >
                    {t("Cancel")}
                </Button>
                {isReject ? (
                    <Button
                        color="danger"
                        outline
                        type="button"
                        onClick={handleConfirm}
                        disabled={loading}
                    >
                        {loading ? (
                            <span className="spinner-border spinner-border-sm me-2" />
                        ) : null}
                        <i className="ri-close-line me-1" />
                        {t("Reject")}
                    </Button>
                ) : (
                    <Button
                        color="success"
                        type="button"
                        onClick={handleConfirm}
                        disabled={loading}
                    >
                        {loading ? (
                            <span className="spinner-border spinner-border-sm me-2" />
                        ) : null}
                        <i className="ri-check-line me-1" />
                        {t("Confirm Approval")}
                    </Button>
                )}
            </ModalFooter>
        </Modal>
    );
};

export default withNamespaces()(ApprovalConfirmationModal);
